import { Component, Input, Output, EventEmitter, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Affaire, AffaireFile } from '../../../core/models/models';
import { AffaireService } from '../../../core/services/affaire.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-affaire-files-dialog',
  standalone: true,
  imports: [CommonModule, MatIconModule, ConfirmDialogComponent],
  template: `
    <div class="modal-overlay" (click)="onClose()">
      <div class="modal-container files-dialog" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3 class="modal-title">Documents de l'affaire {{ affaire?.numeroAffaire }}</h3>
          <button class="close-btn" (click)="onClose()">×</button>
        </div>

        <div class="modal-body">
          <div class="file-upload-header">
            <p class="section-subtitle">Gérez les pièces jointes liées à cette affaire</p>
            <label for="fileDialogInput" class="btn btn-secondary btn-sm btn-with-icon">
              <mat-icon aria-hidden="true">attach_file</mat-icon>
              Ajouter un document
            </label>
            <input type="file" id="fileDialogInput" (change)="onFileSelected($event)" style="display: none;">
          </div>

          @if (isUploading()) {
            <div class="upload-status">Envoi en cours...</div>
          }

          @if (errorMessage()) {
            <div class="error-banner">{{ errorMessage() }}</div>
          }

          <div class="file-list-container">
            @for (file of affaire?.files; track file.id) {
              <div class="file-item clickable" (click)="onPreviewFile(file)">
                <div class="file-icon">
                  @if (isImage(file.fileType)) { <mat-icon aria-hidden="true">image</mat-icon> }
                  @else if (isPdf(file.fileType)) { <mat-icon aria-hidden="true">picture_as_pdf</mat-icon> }
                  @else { <mat-icon aria-hidden="true">description</mat-icon> }
                </div>
                <div class="file-info">
                  <div class="file-name">{{ file.fileName }}</div>
                  <div class="file-type">{{ file.fileType }}</div>
                </div>
                <div class="file-actions" (click)="$event.stopPropagation()">
                  <button (click)="onDownloadFile(file)" class="btn-icon download" title="Télécharger">
                    <mat-icon aria-hidden="true">download</mat-icon>
                  </button>
                  <button (click)="onDeleteFile(file)" class="btn-icon delete" title="Supprimer">
                    <mat-icon aria-hidden="true">delete</mat-icon>
                  </button>
                </div>
              </div>
            } @empty {
              <div class="empty-files">
                <div class="empty-icon">
                  <mat-icon aria-hidden="true">folder_open</mat-icon>
                </div>
                <p>Aucun fichier lié à cette affaire.</p>
              </div>
            }
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-primary" (click)="onClose()">Fermer</button>
        </div>
      </div>
    </div>

    <!-- Modal de visualisation -->
    @if (previewFile()) {
      <div class="modal-overlay preview-modal" (click)="closePreview()">
        <div class="modal-container preview-container" (click)="$event.stopPropagation()">
          <div class="modal-header">
            <h3 class="modal-title">{{ previewFile()?.fileName }}</h3>
            <button class="close-btn" (click)="closePreview()">×</button>
          </div>
          <div class="modal-body preview-body">
            @if (isPreviewLoading()) {
              <div class="loading-spinner">Chargement de l'aperçu...</div>
            } @else if (previewUrl()) {
              @if (isImage(previewFile()!.fileType)) {
                <img [src]="previewUrl()" alt="Aperçu" class="preview-image">
              } @else if (isPdf(previewFile()!.fileType)) {
                <iframe [src]="previewUrl()" width="100%" height="500px"></iframe>
              } @else {
                <div class="no-preview">
                  <p>L'aperçu n'est pas disponible pour ce type de fichier.</p>
                  <button class="btn btn-primary" (click)="onDownloadFile(previewFile()!)">Télécharger pour voir</button>
                </div>
              }
            }
          </div>
        </div>
      </div>
    }

    <!-- Dialogue de confirmation de suppression -->
    @if (showDeleteFileModal()) {
      <app-confirm-dialog
        title="Supprimer le fichier"
        [message]="'Êtes-vous sûr de vouloir supprimer le fichier ' + (fileToDelete()?.fileName || '') + ' ?'"
        (confirmed)="confirmDeleteFile()"
        (cancelled)="cancelDeleteFile()">
      </app-confirm-dialog>
    }
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100%;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(2px);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }
    .modal-container {
      background: white;
      border-radius: 12px;
      width: 90%;
      max-width: 600px;
      box-shadow: 0 10px 25px rgba(0,0,0,0.1);
      display: flex;
      flex-direction: column;
      max-height: 85vh;
      animation: modalSlideUp 0.3s ease-out;
    }
    @keyframes modalSlideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    .modal-header {
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid #eee;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .modal-title { margin: 0; font-size: 1.25rem; color: #1a237e; }
    .close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #666; }

    .modal-body {
      padding: 1.5rem;
      overflow-y: auto;
    }

    .file-upload-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;
      background: #f8f9fa;
      padding: 1rem;
      border-radius: 8px;
    }
    .section-subtitle { margin: 0; font-size: 0.9rem; color: #666; }

    .file-list-container {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .file-item {
      display: flex;
      align-items: center;
      padding: 0.75rem 1rem;
      border: 1px solid #eee;
      border-radius: 8px;
      transition: all 0.2s;
    }
    .file-item.clickable { cursor: pointer; }
    .file-item:hover { background: #fcfcfd; border-color: #3f51b5; }

    .file-icon { font-size: 1.5rem; margin-right: 1rem; color: #667085; display: inline-flex; align-items: center; }
    .file-info { flex: 1; }
    .file-name { font-weight: 600; color: #333; font-size: 0.95rem; }
    .file-type { font-size: 0.8rem; color: #888; }

    .file-actions { display: flex; gap: 0.5rem; }
    .btn-icon {
      background: none; border: none; cursor: pointer;
      padding: 0.4rem; border-radius: 6px; transition: background 0.2s; display: inline-flex; align-items: center; justify-content: center;
    }
    .btn-icon:hover { background: #f2f4f7; }
    .btn-icon.delete:hover { background: #fee4e2; }

    .empty-files {
      text-align: center;
      padding: 3rem 1rem;
      color: #888;
    }
    .empty-icon { font-size: 3rem; margin-bottom: 1rem; opacity: 0.6; color: #98a2b3; display: inline-flex; align-items: center; justify-content: center; }

    .upload-status {
      text-align: center; padding: 0.5rem; color: #3f51b5; font-weight: 600;
    }
    .error-banner {
      background: #ffebee; color: #c62828; padding: 0.75rem; border-radius: 4px; margin-bottom: 1rem;
    }

    .modal-footer {
      padding: 1.25rem 1.5rem;
      border-top: 1px solid #eee;
      display: flex;
      justify-content: flex-end;
    }

    /* Modal de prévisualisation */
    .preview-modal { z-index: 1100; }
    .preview-container { max-width: 800px; width: 90%; max-height: 90vh; }
    .preview-body {
      padding: 0; min-height: 300px; background: #f0f0f0;
      display: flex; justify-content: center; align-items: center;
    }
    .preview-image { max-width: 100%; max-height: 70vh; object-fit: contain; }
    .loading-spinner, .no-preview { padding: 2rem; text-align: center; }
  `]
})
export class AffaireFilesDialogComponent {
  @Input() affaire: Affaire | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();

  private affaireService = inject(AffaireService);
  private sanitizer = inject(DomSanitizer);

  isUploading = signal(false);
  errorMessage = signal<string | null>(null);

  // File deletion state
  showDeleteFileModal = signal(false);
  fileToDelete = signal<AffaireFile | null>(null);

  // File preview state
  previewFile = signal<AffaireFile | null>(null);
  previewUrl = signal<SafeResourceUrl | null>(null);
  isPreviewLoading = signal(false);

  onClose() {
    this.close.emit();
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file && this.affaire?.id) {
      this.isUploading.set(true);
      this.errorMessage.set(null);
      this.affaireService.uploadFile(this.affaire.id, file).subscribe({
        next: () => {
          this.refresh.emit();
          this.isUploading.set(false);
          event.target.value = '';
        },
        error: () => {
          this.errorMessage.set('Erreur lors de l\'envoi du fichier');
          this.isUploading.set(false);
        }
      });
    }
  }

  onDeleteFile(file: AffaireFile) {
    this.fileToDelete.set(file);
    this.showDeleteFileModal.set(true);
  }

  confirmDeleteFile() {
    const file = this.fileToDelete();
    if (file && file.id) {
      this.affaireService.deleteFile(file.id).subscribe({
        next: () => {
          this.refresh.emit();
          this.cancelDeleteFile();
        },
        error: () => {
          this.errorMessage.set('Erreur lors de la suppression du fichier');
          this.cancelDeleteFile();
        }
      });
    }
  }

  cancelDeleteFile() {
    this.showDeleteFileModal.set(false);
    this.fileToDelete.set(null);
  }

  onDownloadFile(file: AffaireFile) {
    if (!file.id) return;
    this.affaireService.downloadFile(file.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.fileName;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.errorMessage.set('Erreur lors du téléchargement du fichier')
    });
  }

  onPreviewFile(file: AffaireFile) {
    if (!file.id) return;
    this.isPreviewLoading.set(true);
    this.previewFile.set(file);
    this.previewUrl.set(null);

    this.affaireService.downloadFile(file.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
        this.isPreviewLoading.set(false);
      },
      error: () => {
        this.errorMessage.set('Erreur lors de la visualisation du fichier');
        this.isPreviewLoading.set(false);
        this.closePreview();
      }
    });
  }

  closePreview() {
    this.previewFile.set(null);
    this.previewUrl.set(null);
  }

  isImage(fileType: string): boolean {
    return fileType.startsWith('image/');
  }

  isPdf(fileType: string): boolean {
    return fileType === 'application/pdf';
  }
}
